import React, { Fragment } from 'react'
import ResizeDetector from 'react-resize-detector'
// @src imports
import { Button, Icon } from 'src/chrome'
import { CardType } from 'src/graphql'
// relative imports
import Panel from './Panel/Panel'
import ImageElement from '../components/ImageElement'
import {
  ComputedFullBleed,
  getSinglePanelIndex,
  getSinglePanelIndexMap,
} from '../../redux/selectors/editor'

import styles from '../styles/components/panel.module.scss'
import { Api } from '../api'
import { Steps } from '../types'

type Props = {
  api: Api
  fullBleed: ComputedFullBleed
}

const elementDisplayStyles: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
}

const arrowStyles: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(50% - 25px)',
  cursor: 'pointer',
}

const FullBleed: React.FC<Props> = props => {
  const { api, fullBleed } = props
  const {
    step,
    panelView,
    fullBleeds,
    card,
    selectPanel,
    selectFullBleed,
    updateFullBleed,
    resetStep,
    resolveLocation,
    resolveNavigation,
  } = api
  const { fullBleedIndex, panelIndex } = step
  const numFullBleeds = fullBleeds.length - 1
  const numPanels =
    card.type === 'THREE_PANEL'
      ? numFullBleeds + 2
      : card.type === 'TWO_PANEL' || card.type === 'TWO_PANEL_BIG'
      ? numFullBleeds + 1
      : numFullBleeds

  const backgroundElement = fullBleed.backgroundElement

  const singlePanelIndex = getSinglePanelIndex(
    fullBleeds,
    fullBleedIndex,
    panelIndex,
  )

  const currentIndex: number =
    panelView === 'fullbleed' ? fullBleedIndex : singlePanelIndex

  const setPanelIndex =
    panelView === 'fullbleed'
      ? selectFullBleed
      : (index: number) => {
          const { fullBleedIndex, panelIndex } = getSinglePanelIndexMap(
            fullBleeds,
          )[index]

          selectPanel(fullBleedIndex, panelIndex)
        }

  const removeSpan = () =>
    updateFullBleed(step.fullBleedIndex, fullBleed => ({
      ...fullBleed,
      backgroundElement: fullBleed.backgroundElement && {
        ...fullBleed.backgroundElement,
        image: {
          image: null,
          position: { x: 0.5, y: 0.5 },
          scale: 1,
          filter: null,
        },
      },
    }))

  return (
    <div id={'fullbleed-container'}>
      <div
        className={`${
          card.isHorizontal
            ? `${styles.fullBleed} ${styles.horizontalCard}`
            : styles.fullBleed
        } ${step.type !== Steps.Idle && styles.openDrawer}`}
      >
        <div css={{ position: 'relative' }}>
          <Fragment>
            {panelView === 'fullbleed' &&
              backgroundElement &&
              (!card.detailedSendableCard ||
                (card.detailedSendableCard &&
                  !card.detailedSendableCard.insideRightImage)) && (
                <Fragment>
                  <div
                    className={
                      card.isHorizontal
                        ? `${styles.fullBleedPanelContainer} ${styles.horizontalContainer}`
                        : styles.fullBleedPanelContainer
                    }
                    style={{
                      ...(panelIndex === null
                        ? {
                            zIndex: 102,
                            background: '#fff',
                          }
                        : {}),
                    }}
                  >
                    <div className={styles.panelContent}>
                      <ImageElement
                        api={api}
                        element={backgroundElement}
                        elementDisplayStyles={elementDisplayStyles}
                      />
                    </div>
                  </div>
                  {panelIndex === undefined &&
                    backgroundElement.image &&
                    backgroundElement.image.image && (
                      <div className={styles.imageSpanActions}>
                        <Button
                          title={'Remove'}
                          className={styles.spanningAction}
                          onClick={() => {
                            removeSpan()
                            resetStep()
                          }}
                        />
                        <Button
                          title={'Done'}
                          className={styles.spanningAction}
                          onClick={() => resetStep()}
                        />
                      </div>
                    )}
                </Fragment>
              )}
          </Fragment>
          {currentIndex > 0 && (
            <div
              id={'carrot_left_btn'}
              onClick={() => setPanelIndex(currentIndex - 1)}
              css={{
                ...arrowStyles,
                left: -70,
                width: 70,
              }}
            >
              <Icon icon={'CARROTLEFT'} style={{ marginLeft: 30 }} />
              <p style={{ textAlign: 'center' }}>
                {'Edit: ' +
                  resolveNavigation(
                    resolveLocation(card.type),
                    card.type,
                    true,
                  )}
              </p>
            </div>
          )}
          {currentIndex <
            (panelView === 'fullbleed' ? numFullBleeds : numPanels) && (
            <div
              id={'carrot_right_btn'}
              onClick={() => setPanelIndex(currentIndex + 1)}
              css={{
                ...arrowStyles,
                right: -70,
                width: 70,
              }}
            >
              <Icon icon={'CARROTRIGHT'} style={{ marginLeft: 30 }} />
              <p style={{ textAlign: 'center' }}>
                {'Edit: ' +
                  resolveNavigation(
                    resolveLocation(card.type),
                    card.type,
                    false,
                  )}
              </p>
            </div>
          )}
          <ResizeDetector
            handleWidth={true}
            handleHeight={true}
            querySelector={'#editor-window'}
          >
            {({
              width: fullContainerWidth,
              height: fullContainerHeight,
            }: {
              width: number
              height: number
            }) => {
              // Cap the width & height so it doesn't disappear on mobile
              const containerWidth = Math.max(fullContainerWidth - 100, 300)
              const containerHeight = Math.max(fullContainerHeight - 75, 500)

              const width =
                panelView === 'fullbleed'
                  ? fullBleed.width
                  : fullBleed.panels[0].width * fullBleed.width
              const height =
                panelView === 'fullbleed'
                  ? fullBleed.height
                  : fullBleed.panels[0].height * fullBleed.height
              const maxByHeight = {
                height: containerHeight,
                width: (containerHeight / height) * width,
              }
              const maxByWidth = {
                height: (containerWidth / width) * height,
                width: containerWidth,
              }
              const canHeightFit =
                maxByHeight.height <= containerHeight &&
                maxByHeight.width <= containerWidth
              const canWidthFit =
                maxByWidth.height <= containerHeight &&
                maxByWidth.width <= containerWidth

              const heightArea = maxByHeight.height * maxByHeight.width
              const widthArea = maxByWidth.height * maxByWidth.width

              const shouldLimitHeight =
                canHeightFit && canWidthFit
                  ? heightArea > widthArea
                  : canHeightFit

              const calculatedWidth = shouldLimitHeight
                ? maxByHeight.width - 100
                : maxByWidth.width - 100
              return (
                <div
                  style={{ width: calculatedWidth }}
                  className={`
                    ${styles.fullBleedPanels}
                    ${card.isHorizontal ? styles.horizontalCard : ''}
                    ${
                      card.type === CardType.THREE_PANEL &&
                      fullBleedIndex === 1 &&
                      !card.isHorizontal
                        ? styles.threePanelFullBleedPortrait
                        : card.type === CardType.THREE_PANEL &&
                          fullBleedIndex === 1 &&
                          card.isHorizontal
                        ? styles.threePanelFullBleedLandscape
                        : ''
                    }
                  `}
                >
                  {fullBleed.panels.map((panel, index) => (
                    <Panel
                      key={panel.location}
                      api={api}
                      fullBleed={fullBleed}
                      panel={panel}
                      index={panel.location}
                      hasBackgroundSpread={
                        !!(
                          backgroundElement &&
                          backgroundElement.image &&
                          backgroundElement.image.image
                        )
                      }
                    />
                  ))}
                </div>
              )
            }}
          </ResizeDetector>
        </div>
      </div>
    </div>
  )
}

export default FullBleed
